import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { StockItemRef } from '@/lib/recipeEditor'

const CAP = 60

/**
 * Searchable ingredient picker (F-FE-N157).
 *
 * Replaces the plain <select> that listed every stock item as
 * "Butter · 12.94 kg left" — a quantity that read as a price, and no search.
 * Each row shows the RATE the recipe will price at (the number that matters
 * here), the unit, and nothing else.
 *
 * Keyboard (audit U-18): ArrowUp / ArrowDown move a highlight that skips items
 * already on the recipe, Enter picks the highlighted item and never a disabled
 * one, Escape closes. The list is capped and says so.
 */
export function IngredientPicker({
  value,
  items,
  onChange,
  onCreateNew,
  formatMoney,
  disabledIds,
  autoOpen,
}: {
  value: string
  items: StockItemRef[]
  onChange: (id: string) => void
  /** Receives what the owner had typed, so the new item starts with that name. */
  onCreateNew?: (query: string) => void
  formatMoney: (n: number) => string
  /** Items already used on another row of the same scope. */
  disabledIds?: Set<string>
  /** Open the search as soon as the row mounts (a freshly added line). */
  autoOpen?: boolean
}) {
  const [open, setOpen] = useState(Boolean(autoOpen))
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const selected = items.find((i) => i.id === value)

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? items.filter((i) => i.name.toLowerCase().includes(s)) : items
  }, [items, q])
  const shown = useMemo(() => matches.slice(0, CAP), [matches])
  const isDisabled = (i: StockItemRef | undefined) => Boolean(i && disabledIds?.has(i.id) && i.id !== value)

  useEffect(() => {
    if (open) {
      setQ('')
      const t = setTimeout(() => inputRef.current?.focus(), 30)
      return () => clearTimeout(t)
    }
  }, [open])

  // Highlight the first pickable match whenever the matches change.
  useEffect(() => {
    const first = shown.findIndex((i) => !(disabledIds?.has(i.id) && i.id !== value))
    setHi(first < 0 ? 0 : first)
  }, [shown, disabledIds, value])

  useEffect(() => {
    if (!open) return
    document.getElementById(`${listId}-opt-${hi}`)?.scrollIntoView({ block: 'nearest' })
  }, [hi, open, listId])

  const move = (delta: 1 | -1) => {
    if (shown.length === 0) return
    let i = hi
    for (let step = 0; step < shown.length; step++) {
      i = i + delta
      if (i < 0 || i >= shown.length) return
      if (!isDisabled(shown[i])) {
        setHi(i)
        return
      }
    }
  }

  const pick = (id: string) => {
    onChange(id)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label="Ingredient"
          className={cn(
            'flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="min-w-0 truncate">
            {selected ? (
              <>
                <span className="font-medium text-foreground">{selected.name}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {selected.unitCost != null ? `${formatMoney(selected.unitCost)} / ${selected.unit}` : `no cost yet · ${selected.unit}`}
                </span>
              </>
            ) : (
              'Pick an ingredient…'
            )}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0">
        <div className="flex items-center gap-2 border-b px-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                move(1)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                move(-1)
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const target = shown[hi]
                if (target && !isDisabled(target)) pick(target.id)
              } else if (e.key === 'Escape') {
                setOpen(false)
              }
            }}
            placeholder="Search ingredients…"
            aria-label="Search ingredients"
            aria-controls={listId}
            aria-activedescendant={shown[hi] ? `${listId}-opt-${hi}` : undefined}
            className="h-9 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <ul id={listId} role="listbox" aria-label="Ingredients" className="max-h-64 overflow-y-auto py-1">
          {shown.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">No ingredient matches “{q}”.</li>
          )}
          {shown.map((i, idx) => {
            const used = isDisabled(i)
            return (
              <li
                key={i.id}
                id={`${listId}-opt-${idx}`}
                role="option"
                aria-selected={idx === hi}
                aria-disabled={used || undefined}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  disabled={used}
                  onMouseEnter={() => !used && setHi(idx)}
                  onClick={() => pick(i.id)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted focus:outline-none disabled:opacity-40',
                    idx === hi && 'bg-muted',
                  )}
                  title={used ? 'Already on this recipe for the same “When”' : undefined}
                >
                  <Check className={cn('h-3.5 w-3.5 shrink-0', i.id === value ? 'opacity-100' : 'opacity-0')} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{i.name}</span>
                  <span className={cn('shrink-0 text-xs tabular-nums', i.unitCost == null ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
                    {i.unitCost != null ? `${formatMoney(i.unitCost)} / ${i.unit}` : `no cost · ${i.unit}`}
                  </span>
                </button>
              </li>
            )
          })}
          {matches.length > CAP && (
            <li role="presentation" className="px-3 py-1.5 text-xs text-muted-foreground">
              Showing {CAP} of {matches.length} — keep typing to narrow it down.
            </li>
          )}
        </ul>
        {onCreateNew && (
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onCreateNew(q.trim())
            }}
            className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> New ingredient{q.trim() ? ` “${q.trim()}”` : ''}
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
